import React from 'react';
import { Form, Modal, Select, Switch, Tooltip, message } from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { RichTextFormField } from '../../components/RichTextEditor';
import {
	CAUGHT_PHASE_OPTIONS,
	DEFAULT_CAUGHT_PHASE,
	DEFAULT_FAULT,
	DEFAULT_SEVERITY,
	SEVERITY_OPTIONS,
	faultOptions,
	hasIssueDescription,
} from './issueFields';
import type { ProgramIssueCaughtPhase } from '../../interfaces/CamProgramming.interface';
import {
	useCreateProgramIssueMutation,
	useGetProgramQuery,
	useUpdateProgramIssueMutation,
} from '../../redux/api/manufacturing';
import { useGetLoggedInUserQuery, useGetUsersQuery } from '../../redux/api/users';
import { useProgramImages } from './useProgramImages';
import type {
	ProgramIssue,
	ProgramIssueFault,
	ProgramIssueSeverity,
} from '../../interfaces/CamProgramming.interface';

interface IssueFormValues {
	severity: ProgramIssueSeverity;
	caught_phase: ProgramIssueCaughtPhase;
	fault: ProgramIssueFault;
	insufficient_training: boolean;
	reported_by: string | null;
	description: string;
}

const FAULT_HINT =
	'Whose work introduced the problem. Leave it on the programmer unless a ' +
	'reviewer changed the program and caused this — the programmer is then not ' +
	'charged for it, and neither is any review that finished before the change.';

const TRAINING_HINT =
	'The mistake came from a training gap rather than carelessness. The person ' +
	'at fault is not charged for it, though the issue stays on the record and ' +
	'still counts toward the shop-wide score.';

const labelWithHint = (label: string, hint: string) => (
	<span className="cam-issue-form__label">
		{label}
		<Tooltip title={hint}>
			<QuestionCircleOutlined className="cam-issue-form__label-hint" />
		</Tooltip>
	</span>
);

interface IssueFormModalProps {
	programId: string;
	/** The issue being edited, or null to log a new one. */
	issue: ProgramIssue | null;
	/** Review the new issue was raised in. Null for one reported on its own. */
	reviewId?: string | null;
	open: boolean;
	onClose: () => void;
}

/**
 * Add / edit dialog for a program issue, shared by the issues list and the
 * threaded review layout so both log an issue the same way.
 */
const IssueFormModal: React.FC<IssueFormModalProps> = ({
	programId,
	issue,
	reviewId = null,
	open,
	onClose,
}) => {
	const [form] = Form.useForm<IssueFormValues>();
	const { data: usersData } = useGetUsersQuery({});
	const { data: currentUser } = useGetLoggedInUserQuery();
	// Named fault options need the people on this program. Cached by the
	// enclosing detail view, so this is normally a read rather than a fetch.
	const { data: program } = useGetProgramQuery(programId, {
		skip: !programId || !open,
	});
	const [createIssue, { isLoading: isCreating }] =
		useCreateProgramIssueMutation();
	const [updateIssue, { isLoading: isUpdating }] =
		useUpdateProgramIssueMutation();
	const images = useProgramImages(programId);

	// The user list is one page of results, so whoever is logged in is not
	// guaranteed to be in it. Without them the prefilled value below would
	// render as a raw id.
	const reporterOptions = React.useMemo(() => {
		const byId = new Map<string, { label: string; value: string }>();
		for (const user of usersData?.results ?? []) {
			byId.set(user.id, {
				label: user.full_name || user.email,
				value: user.id,
			});
		}
		if (currentUser?.id && !byId.has(currentUser.id)) {
			byId.set(currentUser.id, {
				label: currentUser.full_name || currentUser.email,
				value: currentUser.id,
			});
		}
		return [...byId.values()];
	}, [usersData?.results, currentUser]);

	// `initialValues` is read once when the dialog mounts, which can happen
	// before the logged-in user resolves. Fill the reporter in when it lands,
	// leaving an answered field alone so a manual pick is never overwritten.
	React.useEffect(() => {
		if (!open || issue || !currentUser?.id) {
			return;
		}
		if (!form.getFieldValue('reported_by')) {
			form.setFieldsValue({ reported_by: currentUser.id });
		}
	}, [open, issue, currentUser?.id, form]);

	const buildPayload = (values: IssueFormValues) => ({
		severity: values.severity,
		caught_phase: values.caught_phase,
		fault: values.fault,
		insufficient_training: !!values.insufficient_training,
		description: values.description,
		...(values.reported_by ? { reported_by: values.reported_by } : {}),
	});

	const handleSubmit = async (values: IssueFormValues) => {
		try {
			if (issue) {
				await updateIssue({
					id: issue.id,
					programId,
					values: buildPayload(values),
				}).unwrap();
				message.success('Issue updated');
			} else {
				await createIssue({
					program: programId,
					review: reviewId,
					...buildPayload(values),
				}).unwrap();
				message.success('Issue logged');
			}
			onClose();
		} catch {
			message.error('Failed to save issue');
		}
	};

	return (
		<Modal
			className="cam-modal"
			title={issue ? 'Edit Issue' : 'Log Issue'}
			open={open}
			centered
			onCancel={onClose}
			onOk={() => form.submit()}
			okText={issue ? 'Save' : 'Log'}
			confirmLoading={isCreating || isUpdating}
			width={780}
			destroyOnClose
		>
			{/* Torn down with the dialog, so the fields re-seed from `issue`
			    every time it reopens. */}
			<Form
				form={form}
				layout="vertical"
				onFinish={handleSubmit}
				initialValues={{
					severity: issue?.severity ?? DEFAULT_SEVERITY,
					caught_phase: issue?.caught_phase ?? DEFAULT_CAUGHT_PHASE,
					fault: issue?.fault ?? DEFAULT_FAULT,
					insufficient_training: issue?.insufficient_training ?? false,
					reported_by: issue?.reported_by?.id ?? currentUser?.id ?? null,
					description: issue?.description || '',
				}}
			>
				<div className="cam-issue-form__row">
					<Form.Item
						name="severity"
						label="Severity"
						rules={[{ required: true, message: 'Select a severity' }]}
						className="cam-issue-form__field cam-issue-form__field--severity"
					>
						<Select options={SEVERITY_OPTIONS} />
					</Form.Item>
					<Form.Item
						name="caught_phase"
						label="Caught In"
						rules={[
							{
								required: true,
								message: 'Select when the issue was caught',
							},
						]}
						className="cam-issue-form__field cam-issue-form__field--phase"
					>
						<Select options={CAUGHT_PHASE_OPTIONS} />
					</Form.Item>
					<Form.Item
						name="reported_by"
						label="Caught By"
						className="cam-issue-form__field cam-issue-form__field--reporter"
					>
						<Select
							allowClear
							showSearch
							optionFilterProp="label"
							placeholder="Select who caught it"
							options={reporterOptions}
						/>
					</Form.Item>
				</div>
				<div className="cam-issue-form__row">
					<Form.Item
						name="fault"
						label={labelWithHint('At Fault', FAULT_HINT)}
						rules={[{ required: true, message: 'Select who is at fault' }]}
						className="cam-issue-form__field cam-issue-form__field--fault"
					>
						<Select options={faultOptions(program)} />
					</Form.Item>
					<Form.Item
						name="insufficient_training"
						label={labelWithHint('Insufficient Training', TRAINING_HINT)}
						valuePropName="checked"
						className="cam-issue-form__field cam-issue-form__field--training"
					>
						<Switch checkedChildren="Yes" unCheckedChildren="No" />
					</Form.Item>
				</div>
				<Form.Item
					name="description"
					label="Description"
					style={{ marginTop: 16 }}
					rules={[
						{
							validator: (_, value: string) =>
								hasIssueDescription(value)
									? Promise.resolve()
									: Promise.reject(new Error('Describe the issue')),
						},
					]}
				>
					<RichTextFormField
						{...images}
						placeholder="Describe the issue... Paste or drop images directly."
						minHeight={180}
					/>
				</Form.Item>
			</Form>
		</Modal>
	);
};

export default IssueFormModal;
